"use client"

import Link from "next/link";
import clsx from "clsx";
import { useLayoutEffect, useRef, useState } from "react";
import { GradientButton } from "../../core/navigation/Navigation";
import PasswordlessSignUp from "@/components/pages/signup/PasswordlessSignUp";
import { useLoggedIn } from "@/helpers/isLoggedIn";

interface CTALink {
  _type: string;
  title: string;
  url: string;
  isExternal?: boolean;
}

export interface CTAProps {
  internalTitle?: string;
  renderStarForFree: boolean;
  showButtonsWhenLoggedIn?: boolean;
  links: CTALink[];
  secondaryButtonStyle?: "gray" | "white";
  className?: string;
  secondaryLabel?: string;
  secondaryLink?: string;
}

const CTA = ({
  renderStarForFree,
  showButtonsWhenLoggedIn = true,
  links,
  secondaryButtonStyle = "gray",
  className,
  secondaryLabel = "Book a demo",
  secondaryLink = "/book-a-demo",
}: CTAProps) => {
  const loggedIn = useLoggedIn();
  const cleanSecondaryButtonStyle = secondaryButtonStyle as "gray" | "white" | undefined;
  const isWhite = cleanSecondaryButtonStyle === "white";

  const openDash0Ref = useRef<HTMLSpanElement>(null);
  const bookDemoRef = useRef<HTMLSpanElement>(null);
  const [equalButtonWidth, setEqualButtonWidth] = useState<number>();
  const [isWidthReady, setIsWidthReady] = useState(false);

  useLayoutEffect(() => {
    if (!(renderStarForFree && loggedIn && showButtonsWhenLoggedIn)) return;

    const updateWidth = () => {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      if (!isDesktop) {
        setEqualButtonWidth(undefined);
        setIsWidthReady(true);
        return;
      }
      const openWidth = openDash0Ref.current?.offsetWidth;
      const demoWidth = bookDemoRef.current?.offsetWidth;
      if (openWidth && demoWidth) {
        setEqualButtonWidth(Math.max(openWidth, demoWidth));
      }
      setIsWidthReady(true);
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [renderStarForFree, loggedIn, showButtonsWhenLoggedIn]);

  if (renderStarForFree) {
    if (!loggedIn) {
      return <PasswordlessSignUp className={className} secondaryLabel={secondaryLabel} secondaryLink={secondaryLink} />;
    }

    if (!showButtonsWhenLoggedIn) {
      return null;
    }

    return (
      <div
        className={clsx([
          "flex flex-col lg:flex-row gap-5 items-center justify-center pt-8 max-md:px-2",
          isWidthReady ? "animate-fade-in-up" : "opacity-0",
          className,
        ])}
      >
        <span
          ref={openDash0Ref}
          className="w-full lg:w-fit"
          style={equalButtonWidth ? { width: equalButtonWidth } : undefined}
        >
          <GradientButton
            className="py-3 h-12 min-h-12 px-5 w-full flex justify-center"
            href="https://app.dash0.com"
          >
            Open Dash0
          </GradientButton>
        </span>
        <span
          ref={bookDemoRef}
          className="w-full lg:w-fit"
          style={equalButtonWidth ? { width: equalButtonWidth } : undefined}
        >
          <Link
            href={secondaryLink}
            className="w-full justify-center relative shrink-0 rounded h-12 min-h-12 items-center text-center py-4 px-5 group font-medium transition-colors duration-200 flex whitespace-nowrap bg-white text-[#111111] hover:bg-grey-200"
          >
            {secondaryLabel}
          </Link>
        </span>
      </div>
    );
  }

  return (
    <div
      className={clsx([
        "flex lg:flex-wrap gap-3 items-center justify-center pt-8 max-md:px-2",
        className,
      ])}
    >
      {links?.length > 0 &&
        links.map((link, index) => {
          const cleanTitle = link.title;
          if (index === 0) {
            return (
              <GradientButton key={index} className="py-3 min-h-10 px-5 w-fit" href={link.url}>
                {cleanTitle}
              </GradientButton>
            );
          }

          return (
            <Link
              key={index}
              href={link.url}
              className={clsx([
                "w-1/2 lg:w-fit justify-center relative shrink-0 rounded h-10 items-center text-center py-4 min-h-10 px-5 group font-medium transition-colors duration-200 flex whitespace-nowrap",
                isWhite
                  ? "bg-white text-[#111111] hover:bg-grey-200"
                  : "bg-grey-850 hover:bg-grey-800",
              ])}
            >
              {cleanTitle}
            </Link>
          );
        })}
    </div>
  );
};

export default CTA;
